import React, { useCallback, useRef, useState } from 'react';
import { App } from 'antd';
import type { InputNode } from '../types';
import {
  PLACEHOLDER_THUMB,
  PlayIcon,
  ProgressRing,
  VideoPreviewTooltip,
  extractVideoFirstFrame,
} from './videoShared';
import { parseStepsTooltip } from '../utils/stepsTooltip';
import { StepsTutorial } from './StepsTutorial';
import { InfoIconTooltip } from './InfoIconTooltip';
import { resolveUploadFileType } from '../../shared/uploadFileType';
import { useTextOverflow } from '../utils/useTextOverflow';
import { SeTooltip } from './SeTooltip';

type Props = {
  node: InputNode;
  value: string | undefined;
  thumbnail?: string;
  onChange: (key: string, value: string) => void;
  onThumbnailChange?: (key: string, thumbnail: string) => void;
  /** 上传成功后把原始 File 存到外层 files[key]，发送时 FormData 用二进制（V1 parity：videoFile 为二进制） */
  onFileChange?: (key: string, file: File | null) => void;
  videoUpload?: (
    file: File,
    onProgress?: (percent: number) => void,
  ) => Promise<{ videoUrl: string; thumbnail: string }>;
  mediaUpload?: (
    file: File,
    onProgress?: (percent: number) => void,
  ) => Promise<{
    mediaUrl: string;
    thumbnail?: string;
    mediaType?: 'audio' | 'video' | 'document';
  }>;
  onParsingChange?: (key: string, parsing: boolean) => void;
  disabled?: boolean;
  error?: string;
};

const normalizeTooltip = (t: InputNode['tooltip']): string => {
  if (!t) return '';
  return typeof t === 'string' ? t : JSON.stringify(t);
};

// V1 parity（RichTextEditor/index.tsx）：视频文件限 1GB，超限 warning 提示且不上传。
const MAX_VIDEO_BYTES = 1024 * 1024 * 1024;
const VIDEO_SIZE_ERROR = '视频文件不能超过1GB';
const MEDIA_SIZE_ERROR = '文件不能超过1GB';
const MEDIA_ACCEPT = 'audio/*,video/*,.doc,.docx,.pdf,.md,.markdown,.xls,.xlsx,.csv';
const DOCUMENT_EXTENSIONS = new Set([
  'doc',
  'docx',
  'pdf',
  'md',
  'markdown',
  'xls',
  'xlsx',
  'csv',
]);

type MediaFileType = 'audio' | 'video' | 'document';

const getMediaFileType = (file: File): MediaFileType | null => {
  const uploadFileType = resolveUploadFileType(file);
  if (uploadFileType === 'audio') return 'audio';
  if (uploadFileType === 'video') return 'video';
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return DOCUMENT_EXTENSIONS.has(extension) ? 'document' : null;
};

/**
 * V1 parity (RichTextEditor/nodes/video/render.ts + hooks.ts):
 * - 空态：button + 上传图标 + label + 可选 .upload-info-icon (tooltip 模式)
 * - loading：抽取首帧作 loading 缩略图 + 圆环进度（来自 videoUpload 的 onProgress）+ fileName label
 * - success：thumbnail（或占位）+ 播放 overlay + fileName + 移除
 * - error：errorMsg + 重试
 * - required 模式：所有 state 外套 .exclusion-wrapper.required-upload-group + ⛤
 * - chip 所有状态都带 data-tooltip + data-group(exclusionGroup) attribute
 */
export const VideoUploadNode: React.FC<Props> = React.memo(
  ({
    node,
    value,
    thumbnail,
    onChange,
    onThumbnailChange,
    onFileChange,
    videoUpload,
    mediaUpload,
    onParsingChange,
    disabled,
    error,
  }) => {
    const { message } = App.useApp();
    const inputRef = useRef<HTMLInputElement>(null);
    const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
    const [percent, setPercent] = useState(0);
    const [loadingThumb, setLoadingThumb] = useState('');
    const [fileName, setFileName] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedMediaType, setSelectedMediaType] = useState<MediaFileType | undefined>(node.mediaType);
    const [fileNameRef, isFileNameOverflowing, checkFileNameOverflow] =
      useTextOverflow<HTMLSpanElement>();

    // steps tooltip 走「查看教程」按钮：empty-state 不渲染 info-icon（tooltip 置空），
    // wrap 在 chip 同级追加 StepsTutorial（有视频时隐藏）。
    const stepsTooltip = parseStepsTooltip(node.tooltip);
    const isSteps = !!stepsTooltip;
    const tooltip = isSteps ? '' : normalizeTooltip(node.tooltip);
    const isMediaUpload = node.type === 'media-upload';
    const uploadFile = isMediaUpload ? mediaUpload : videoUpload;
    const acceptedFileTypes = isMediaUpload ? MEDIA_ACCEPT : 'video/*';
    const hasMedia = !!value;
    const isAudio = isMediaUpload && selectedMediaType === 'audio';
    const isDocument = isMediaUpload && selectedMediaType === 'document';
    const isVideo = !isAudio && !isDocument;
    const isLoading = status === 'loading';

    const pick = useCallback(() => {
      if (disabled || isLoading) return;
      if (!isMediaUpload && !uploadFile) return;
      inputRef.current?.click();
    }, [disabled, isLoading, isMediaUpload, uploadFile]);

    const onFile = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;

        if (f.size > MAX_VIDEO_BYTES) {
          message.warning(isMediaUpload ? MEDIA_SIZE_ERROR : VIDEO_SIZE_ERROR);
          return;
        }

        const nextMediaType = isMediaUpload ? getMediaFileType(f) : 'video';
        if (!nextMediaType) {
          message.warning('仅支持音频、视频、Word、PDF、Markdown 或 Excel 文件');
          return;
        }

        setFileName(f.name);
        setSelectedMediaType(nextMediaType);

        if (isMediaUpload && !uploadFile) {
          onChange(node.key!, f.name);
          onFileChange?.(node.key!, f);
          setStatus('idle');
          if (nextMediaType === 'video') {
            void extractVideoFirstFrame(f)
              .then((videoThumbnail) => onThumbnailChange?.(node.key!, videoThumbnail))
              .catch(() => onThumbnailChange?.(node.key!, ''));
          } else {
            onThumbnailChange?.(node.key!, '');
          }
          return;
        }

        if (!uploadFile) return;

        setStatus('loading');
        setPercent(0);
        setErrorMsg('');
        setLoadingThumb('');
        onParsingChange?.(node.key!, true);

        const localThumb = nextMediaType === 'video'
          ? await extractVideoFirstFrame(f).catch(() => '')
          : '';
        if (localThumb) setLoadingThumb(localThumb);

        try {
          const uploadResult = await uploadFile(f, (progress) => setPercent(progress));
          const mediaUrl = 'mediaUrl' in uploadResult ? uploadResult.mediaUrl : uploadResult.videoUrl;
          const resultMediaType = 'mediaType' in uploadResult ? uploadResult.mediaType : undefined;
          onChange(node.key!, mediaUrl);
          onThumbnailChange?.(node.key!, uploadResult.thumbnail || localThumb || '');
          onFileChange?.(node.key!, f);
          setSelectedMediaType(resultMediaType || nextMediaType);
          setStatus('idle');
          setLoadingThumb('');
        } catch (err) {
          setStatus('error');
          setErrorMsg(err instanceof Error ? err.message : '上传失败');
          setLoadingThumb('');
        } finally {
          onParsingChange?.(node.key!, false);
        }
      },
      [
        uploadFile,
        isMediaUpload,
        node.key,
        onChange,
        onThumbnailChange,
        onFileChange,
        onParsingChange,
        message,
      ],
    );

    const clear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(node.key!, '');
        onThumbnailChange?.(node.key!, '');
        onFileChange?.(node.key!, null);
        setFileName('');
        setSelectedMediaType(node.mediaType);
        setStatus('idle');
      },
      [node.key, node.mediaType, onChange, onThumbnailChange, onFileChange],
    );

    // V1 parity:required 模式所有 state 外套 .exclusion-wrapper.required-upload-group + ⛤
    // steps tooltip 时在 chip 同级追加「查看教程」按钮（有视频时隐藏）。
    const wrap = (chip: React.ReactElement): React.ReactElement => {
      const tutorial =
        isSteps && stepsTooltip ? (
          <StepsTutorial data={stepsTooltip} hidden={hasMedia} />
        ) : null;
      if (!node.required) {
        return tutorial ? (
          <>
            {chip}
            {tutorial}
          </>
        ) : (
          chip
        );
      }
      return (
        <>
          <span
            className="exclusion-wrapper required-upload-group"
            data-group={node.exclusionGroup}
          >
            <span className="rte-text-required">
              <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
            </span>
            {chip}
          </span>
          {tutorial}
        </>
      );
    };

    // success: 图片/视频缩略图或文件类型图标 + 文件名 + 移除按钮
    if (hasMedia) {
      const displayName = fileName || (isAudio ? '音频文件' : isDocument ? '话术文件' : '视频文件');
      const mediaFileIcon = isAudio ? 'icon-yinpin' : 'icon-wenjian';
      const canPreviewVideo = isVideo && /^(https?:|blob:)/i.test(value!);
      const fileNameLabel = (
        <span
          ref={fileNameRef}
          className="video-label"
          data-file-name={displayName}
          onMouseEnter={checkFileNameOverflow}
        >
          {displayName}
        </span>
      );
      const videoThumbnail = (
        <span className="video-thumbnail-wrapper">
          {thumbnail ? (
            <img
              src={thumbnail}
              className="video-thumbnail rte-video-thumb"
              alt="thumbnail"
            />
          ) : (
            <span className="video-thumbnail video-thumbnail-placeholder">
              <i
                className="iconfont icon-shipin"
                style={{ fontSize: 20, color: '#8c8c8c' }}
              />
            </span>
          )}
          {isVideo && <PlayIcon />}
        </span>
      );
      const fileNameDisplay = isMediaUpload ? (
        <SeTooltip
          title={isFileNameOverflowing ? displayName : ''}
          align={{ offset: [0, -8] }}
          mouseLeaveDelay={0}
        >
          {fileNameLabel}
        </SeTooltip>
      ) : (
        fileNameLabel
      );
      const chip = (
        <span
          className={`video-chip ${error ? 'rte-video-error' : ''}`}
          data-type={node.type}
          data-key={node.key}
          data-tooltip={tooltip || undefined}
          data-group={node.exclusionGroup}
        >
          <span className="video-inner-wrapper video-upload-inner has-video">
            {isAudio || isDocument ? (
              <i className={`iconfont ${mediaFileIcon} media-upload-file-icon`} />
            ) : canPreviewVideo ? (
              <VideoPreviewTooltip videoUrl={value!}>
                {videoThumbnail}
              </VideoPreviewTooltip>
            ) : (
              videoThumbnail
            )}
            {fileNameDisplay}
            <span
              className="video-remove-btn rte-video-remove"
              title={isAudio ? '移除音频' : isDocument ? '移除文件' : '移除视频'}
              onClick={clear}
            >
              ×
            </span>
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={acceptedFileTypes}
            style={{ display: 'none' }}
            onClick={(event) => event.stopPropagation()}
            onChange={onFile}
          />
        </span>
      );
      return wrap(chip);
    }

    // loading: 首帧（或灰底） + overlay + 进度环 + 文件名
    if (isLoading) {
      return wrap(
        <span
          className={`video-chip ${error ? 'rte-video-error' : ''}`}
          data-type={node.type}
          data-key={node.key}
          data-tooltip={tooltip || undefined}
          data-group={node.exclusionGroup}
        >
          <span className="video-inner-wrapper video-upload-inner video-uploading">
            <span className="video-thumbnail-wrapper">
              <img
                className="video-thumbnail"
                src={loadingThumb || PLACEHOLDER_THUMB}
                alt="thumbnail"
              />
              <span className="video-thumbnail-overlay" />
              <ProgressRing percent={percent} />
            </span>
            <span className="video-label">{fileName || '上传中...'}</span>
          </span>
        </span>,
      );
    }

    // error: 红边 chip + errorMsg + 重试（×）
    if (status === 'error') {
      return wrap(
        <span
          className="video-chip rte-video-error"
          data-type={node.type}
          data-key={node.key}
          data-tooltip={tooltip || undefined}
          data-group={node.exclusionGroup}
        >
          <span className="video-inner-wrapper video-upload-inner">
            <span className="video-label">{errorMsg || '上传失败'}</span>
            <span
              className="video-remove-btn rte-video-remove"
              title="重试"
              onClick={(e) => {
                e.stopPropagation();
                setStatus('idle');
                setErrorMsg('');
              }}
            >
              ×
            </span>
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={acceptedFileTypes}
            style={{ display: 'none' }}
            onClick={(event) => event.stopPropagation()}
            onChange={onFile}
          />
        </span>,
      );
    }

    // empty: button + 上传图标 + label + 可选 .upload-info-icon (tooltip 模式)
    return wrap(
      <button
        type="button"
        className={`video-chip ${error ? 'rte-video-error' : ''}`}
        data-type={node.type}
        data-key={node.key}
        data-tooltip={tooltip || undefined}
        data-group={node.exclusionGroup}
        onClick={pick}
        disabled={disabled}
      >
        <span className="video-inner-wrapper video-upload-inner">
          <i className="iconfont icon-shangchuan1" style={{ fontSize: 16 }} />
          <span className="video-label">
            {node.text ?? (isMediaUpload ? '请上传本地音频或视频文件' : '请上传想要分析的视频文件')}
          </span>
          {tooltip && (
            <InfoIconTooltip
              tooltip={tooltip}
              className="upload-info-icon"
              dataUploadKey={node.key}
            />
          )}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes}
          style={{ display: 'none' }}
          onClick={(event) => event.stopPropagation()}
          onChange={onFile}
        />
      </button>,
    );
  },
);
VideoUploadNode.displayName = 'VideoUploadNode';
